import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle, Loader2, Users2, ArrowRight } from "lucide-react";

const PLAN_NAMES = { base: "Base", pro: "Pro", business: "Business" };

export default function PaymentResult() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);
  const cancelled = location.pathname.includes("cancel");

  useEffect(() => {
    if (cancelled) { setState("cancel"); return; }
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    const poll = async () => {
      if (attempts.current >= 8) { setState("error"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { await refresh(); setState("success"); return; }
      } catch (e) {
        console.error("Payment status polling failed:", e);
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9] px-6 noise-overlay">
      <div className="bg-white border border-stone-200 rounded-sm p-12 text-center max-w-md w-full" data-testid="payment-result">
        {state === "checking" && <><Loader2 size={40} className="mx-auto text-brand animate-spin mb-4" /><p className="font-serif text-2xl text-stone-800">Verifica del pagamento…</p></>}
        {state === "success" && (
          <div data-testid="payment-success">
            <CheckCircle2 size={48} className="mx-auto text-brand mb-4" />
            <p className="font-serif text-3xl text-stone-900">Abbonamento attivo!</p>
            <p className="text-sm text-stone-500 mt-2">
              {user?.company_name ? <><b>{user.company_name}</b> è ora sul piano </> : "Sei ora sul piano "}
              <b className="text-brand">{PLAN_NAMES[user?.plan] || user?.plan || "attivo"}</b>
              {user?.max_users ? <> · fino a {user.max_users} utenti</> : null}.
            </p>
            <div className="bg-brand/5 border border-brand/20 rounded-sm p-4 mt-6 text-left flex items-start gap-3">
              <Users2 size={20} className="text-brand mt-0.5 shrink-0" />
              <p className="text-sm text-stone-600">Invita subito la tua squadra: aggiungi i membri del team così potete lavorare insieme sui costi.</p>
            </div>
            <button data-testid="goto-team-btn" onClick={() => navigate("/team")}
              className="mt-6 w-full rounded-full bg-brand text-white py-3 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-brand-hover transition-colors">
              Aggiungi membri del team <ArrowRight size={16} />
            </button>
            <button data-testid="goto-dashboard-btn" onClick={() => navigate("/")}
              className="mt-3 w-full rounded-full border border-stone-300 text-stone-600 py-3 text-sm font-semibold hover:bg-stone-50 transition-colors">
              Vai alla dashboard
            </button>
          </div>
        )}
        {state === "cancel" && <><XCircle size={44} className="mx-auto text-alert mb-4" /><p className="font-serif text-3xl text-stone-900">Pagamento annullato</p><button onClick={() => navigate("/billing")} className="mt-6 rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold">Riprova</button></>}
        {state === "error" && <><XCircle size={44} className="mx-auto text-alert mb-4" /><p className="font-serif text-2xl text-stone-900">Verifica non riuscita</p><button onClick={() => navigate("/billing")} className="mt-6 rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold">Torna ai piani</button></>}
      </div>
    </div>
  );
}
